import React, { useState, useEffect } from "react";
import {
  UserIcon,
  PhotoIcon,
  TagIcon,
  LinkIcon,
  PencilIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { getAuth } from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../firebase";

export default function PromotionalProfileForm({  isOpen, onClose, onCloseAndRefresh, amountUSD, centralizedExpirationDate, planType, postsPerMonth }) {
  const [maxProfiles, setMaxProfiles] = useState(0);
  const [formData, setFormData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);

  const auth = getAuth();
  const db = getFirestore();

  useEffect(() => {
    const fetchMaxProfiles = async () => {
      try {
        const user = auth.currentUser;
        if (!user) throw new Error("Usuario no autenticado");

        const promoProfilesRef = collection(db, "promoProfiles");
        const q = query(
          promoProfilesRef,
          where("userId", "==", user.uid),
          where("status", "==", "COMPLETED")
        );
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const docData = querySnapshot.docs[0].data();
          if (docData.profileCount && typeof docData.profileCount === "number") {
            setMaxProfiles(docData.profileCount);
            setFormData(Array(docData.profileCount).fill({}));
          } else {
            throw new Error("Campo profileCount no válido en la orden.");
          }
        } else {
          throw new Error("No se encontró una orden completada para este usuario.");
        }
      } catch (err) {
        console.error("Error al obtener cantidad máxima de perfiles:", err);
        setError("No se pudo obtener la cantidad máxima de perfiles.");
      }
    };

    fetchMaxProfiles();
  }, []);
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose(); // Cierra el modal al presionar Escape
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  const handleChange = (e, index) => {
    const { name, value, files } = e.target;
    setFormData((prev) => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        [name]: files ? files[0] : value,
      };
      return updated;
    });
  };

  const removeImage = (index) => {
    setFormData((prev) => {
      const updated = [...prev];
      if (updated[index]) {
        updated[index] = { ...updated[index], profilePhoto: null };
      }
      return updated;
    });
  };

  const reloadPage = () => {
    window.location.reload();
  };

  const uploadProfileImage = async (file) => {
  if (!file) return null;

  try {
    // Crear ruta única en Firebase Storage
    const filePath = `promoProfiles/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, filePath);

    // Subir archivo
    const snapshot = await uploadBytes(storageRef, file);

    // Obtener URL pública
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error("Error al subir la imagen de perfil:", error);
    throw new Error("Error al subir la imagen de perfil a Firebase Storage");
  }
};

  const validateProfiles = () => {
    const errors = {};
    formData.forEach((profile, i) => {
      const profileErrors = {};
      if (!profile.name || profile.name.trim() === "")
        profileErrors.name = "Nombre obligatorio";
      if (!profile.description || profile.description.trim() === "")
        profileErrors.description = "Descripción obligatoria";
      if (Object.keys(profileErrors).length > 0) errors[i] = profileErrors;
    });
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    setLoading(true);

    try {
      const user = auth.currentUser;
      if (!user) throw new Error("Usuario no autenticado");

      const validationErrors = validateProfiles();
      if (Object.keys(validationErrors).length > 0) {
        setFieldErrors(validationErrors);
        setLoading(false);
        return;
      }

      const userId = user.uid;

      for (let i = 0; i < maxProfiles; i++) {
        const profile = formData[i];
        let profilePhotoUrl = null;

        if (profile.profilePhoto instanceof File) {
          profilePhotoUrl = await uploadProfileImage(profile.profilePhoto);
        }

        const profileData = {
          name: profile.name || "",
          description: profile.description || "",
          tags: profile.tags
            ? profile.tags.split(",").map((tag) => tag.trim()).filter(Boolean)
            : [],
          profilePhotoUrl,
          externalLinks: profile.externalLinks || null,
          createdAt: new Date(),
          amountUSD: amountUSD || 0,
          centralizedExpirationDate: centralizedExpirationDate
            ? new Date(centralizedExpirationDate)
            : null,
          planType: planType || "",
          postsPerMonth: postsPerMonth || 0,
          status: "COMPLETED",
          userId,
        };

        const promoProfilesRef = collection(db, "users", userId, "promoProfiles");
        const newProfileDocRef = doc(promoProfilesRef);
        await setDoc(newProfileDocRef, profileData);
      }

      setLoading(false);
      setSuccessMessage("¡Perfiles promocionales creados exitosamente!");
      setFormData(Array(maxProfiles).fill({}));
    } catch (err) {
      setLoading(false);
      setError(err.message || "Error al crear perfiles");
    }
  };
  
  const renderForms = () => {
    return formData.map((data, i) => {
      const previewUrl =
        data?.profilePhoto instanceof File
          ? URL.createObjectURL(data.profilePhoto)
          : null;

      return (
        <div
          key={i}
          className="p-6 bg-white dark:bg-gray-800 rounded-lg shadow-md mb-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-gray-100">
            Perfil #{i + 1}
          </h3>

          <div className="space-y-4">
            <div>
              <label
                htmlFor={`name-${i}`}
                className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                <UserIcon className="w-5 h-5 mr-1 text-blue-500" />
                Nombre
              </label>
              <input
                id={`name-${i}`}
                name="name"
                type="text"
                value={formData[i]?.name || ""}
                onChange={(e) => handleChange(e, i)}
                placeholder="Tu nombre o nombre del perfil"
                className={`peer block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-transparent focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition ${
                  fieldErrors[i]?.name
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : ""
                }`}
              />
              <label
                htmlFor={`name-${i}`}
                className="absolute left-3 -top-2.5 text-xs text-gray-500 dark:text-gray-400 transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-300"
              >
                
              </label>
              {fieldErrors[i]?.name && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors[i].name}</p>
              )}
            </div>

            <div>
              <label
                htmlFor={`description-${i}`}
                className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                <PencilIcon className="w-5 h-5 mr-1 text-green-500" />
                Descripción
              </label>
              <textarea
                id={`description-${i}`}
                name="description"
                rows={3}
                value={formData[i]?.description || ""}
                onChange={(e) => handleChange(e, i)}
                placeholder="Describe tu perfil"
                className={`peer block w-full resize-none rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-transparent focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500 transition ${
                  fieldErrors[i]?.description
                    ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                    : ""
                }`}
              />
              <label
                htmlFor={`description-${i}`}
                className="absolute left-3 -top-2.5 text-xs text-gray-500 dark:text-gray-400 transition-all peer-placeholder-shown:top-2 peer-placeholder-shown:text-gray-400 peer-placeholder-shown:text-base peer-placeholder-shown:text-gray-300"
              >
                
              </label>
              {fieldErrors[i]?.description && (
                <p className="text-red-500 text-sm mt-1">{fieldErrors[i].description}</p>
              )}
            </div>

            <div>
              <label
                htmlFor={`tags-${i}`}
                className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                <TagIcon className="w-5 h-5 mr-1 text-purple-500" />
                Etiquetas
              </label>
              <input
                id={`tags-${i}`}
                name="tags"
                type="text"
                value={formData[i]?.tags || ""}
                onChange={(e) => handleChange(e, i)}
                placeholder="Palabras clave separadas por comas"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-gray-900 dark:text-gray-100 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500 transition"
              />
            </div>

            <div>
            <label
              htmlFor={`profilePhoto-${i}`}
              className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
            >
              <PhotoIcon className="w-5 h-5 mr-1 text-pink-500" />
              Foto de Perfil
            </label>

            <div className="flex items-center space-x-4">
              {/* Estilo de botón personalizado para input file */}
              <div>
                <input
                  type="file"
                  id={`profilePhoto-${i}`}
                  name="profilePhoto"
                  accept="image/*"
                  onChange={(e) => handleChange(e, i)}
                  className="hidden"
                />
                <label
                  htmlFor={`profilePhoto-${i}`}
                  className="inline-block cursor-pointer px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow transition"
                >
                  Seleccionar Imagen
                </label>
              </div>

              {/* Vista previa con botón para eliminar */}
              {formData[i]?.profilePhoto && !(formData[i].profilePhoto instanceof File === false && typeof formData[i].profilePhoto !== "string") && (
                <div className="relative w-28 h-28 rounded-full overflow-hidden border-2 border-blue-500 shadow-lg">
                  <img
                    src={
                      typeof formData[i].profilePhoto === "string"
                        ? formData[i].profilePhoto
                        : URL.createObjectURL(formData[i].profilePhoto)
                    }
                    alt={`Vista previa perfil ${i + 1}`}
                    className="object-cover w-full h-full"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(i)}
                    className="absolute top-0 right-0 bg-red-600 hover:bg-red-700 text-white rounded-full p-1 transform translate-x-1/2 -translate-y-1/2 shadow-lg focus:outline-none"
                    aria-label="Eliminar imagen"
                    title="Eliminar imagen"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

            <div>
              <label
                htmlFor={`externalLinks-${i}`}
                className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
              >
                <LinkIcon className="w-5 h-5 mr-1 text-yellow-500" />
                Redes Externas (opcional)
              </label>
              <input
                id={`externalLinks-${i}`}
                name="externalLinks"
                type="text"
                value={formData[i]?.externalLinks || ""}
                onChange={(e) => handleChange(e, i)}
                placeholder="URLs a tus redes o web"
                className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-transparent px-3 py-2 text-gray-900 dark:text-gray-100 focus:border-yellow-500 focus:outline-none focus:ring-1 focus:ring-yellow-500 transition"
              />
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    
    <div className="fixed inset-0 bg-black bg-opacity-30 dark:bg-opacity-70 flex items-center justify-center p-4 z-50">
      <div className="relative bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto p-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">
          Crear Perfiles Promocionales ({maxProfiles})
        </h2>

        {/* Alertas */}
        {error && (
          <div className="mb-6 p-4 rounded-md bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300 shadow-md flex items-center justify-between">
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
              className="text-red-700 dark:text-red-300 hover:text-red-900 dark:hover:text-red-100 focus:outline-none"
              aria-label="Cerrar alerta de error"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 p-4 rounded-md bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 shadow-md flex items-center justify-between animate-fadeIn">
            <p>{successMessage}</p>
          </div>
        )}

        <div className="relative transition-all duration-500 ease-in-out">
          {successMessage ? (
            <div className="text-center py-12 animate-fadeIn transition-all duration-500 ease-in-out transform scale-100 opacity-100">
              <h3 className="text-3xl font-extrabold text-green-600 dark:text-green-400 mb-4">
                ¡Perfiles creados con éxito!
              </h3>
              <p className="text-gray-700 dark:text-gray-300 text-lg max-w-xl mx-auto mb-6">
                Ahora puedes administrar tus perfiles promocionales desde tu página del perfil principal. ¡Gracias por compartir tu talento o emprendimiento con los usuarios de SIT! 🌟
              </p>
              <div className="mt-6 flex justify-center">
                <button
                  onClick={reloadPage}
                  className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 dark:bg-green-500 dark:hover:bg-green-600"
                >
                  Actualizar y volver al inicio
                </button>
              </div>
            </div>
            
          ) : (
            <form
              onSubmit={handleSubmit}
              className="space-y-6 transform transition-all duration-500 ease-in-out scale-100 opacity-100"
            >
              {renderForms()}

              <div className="flex justify-end mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-3 rounded-lg shadow-md transition-colors flex items-center ${
                    loading ? "opacity-70 cursor-not-allowed" : ""
                  }`}
                >
                  {loading ? "Creando perfiles..." : "Crear Perfiles"}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}